import { useCallback, useEffect, useState } from "react";
import { Shield, Save, RotateCcw, Lock, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { getPermissions, updatePermissions } from "@/lib/api";

// Grupos legíveis para agrupar as permissões
const PERM_GROUPS = [
  {
    title: "Dashboards (visualização)",
    items: [
      { key: "visao_geral.view", label: "Visão Geral" },
      { key: "condominios.view", label: "Condomínios" },
      { key: "funil.view", label: "Funil Semanal" },
      { key: "clientes.view", label: "Clientes" },
      { key: "prospectos.view", label: "Prospectos" },
      { key: "planos.view", label: "Planos" },
    ],
  },
  {
    title: "Edição de dados",
    items: [
      { key: "condominios.edit", label: "Editar condomínios" },
      { key: "funil.edit", label: "Editar funil" },
      { key: "clientes.edit", label: "Editar clientes" },
      { key: "prospectos.edit", label: "Editar prospectos" },
      { key: "planos.edit", label: "Editar planos & bônus" },
    ],
  },
  {
    title: "Administração",
    items: [
      { key: "usuarios.manage", label: "Gerenciar usuários" },
      { key: "permissoes.manage", label: "Gerenciar permissões" },
      { key: "recibos.send", label: "Enviar recibos por e-mail" },
    ],
  },
];

const ROLES = [
  { key: "admin", label: "Admin", locked: true, color: "text-primary" },
  { key: "tecnico", label: "Técnico", locked: false, color: "text-sky-500" },
  { key: "vendedora", label: "Vendedor", locked: false, color: "text-emerald-500" },
];

export default function PermissionsMatrix({ open, onOpenChange }) {
  const [roles, setRoles] = useState({ admin: [], tecnico: [], vendedora: [] });
  const [allPerms, setAllPerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initial, setInitial] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPermissions();
      setRoles(r.roles || {});
      setAllPerms(r.all || []);
      setInitial(JSON.stringify(r.roles || {}));
    } catch (err) {
      console.error("Erro ao carregar permissões:", err);
      toast.error("Erro ao carregar permissões");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (role, perm) => {
    if (role === "admin") return; // sempre travado
    setRoles((prev) => {
      const cur = new Set(prev[role] || []);
      if (cur.has(perm)) cur.delete(perm);
      else cur.add(perm);
      return { ...prev, [role]: [...cur] };
    });
  };

  const dirty = initial !== null && JSON.stringify(roles) !== initial;

  const handleReset = () => {
    if (initial) setRoles(JSON.parse(initial));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await updatePermissions({ roles });
      setRoles(r.roles);
      setInitial(JSON.stringify(r.roles));
      toast.success("Permissões salvas");
    } catch (err) {
      console.error("Erro ao salvar permissões:", err);
      toast.error("Erro ao salvar permissões");
    } finally {
      setSaving(false);
    }
  };

  const has = (role, perm) => (roles[role] || []).includes(perm);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-permissoes">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" /> Permissões por papel
          </DialogTitle>
          <DialogDescription>
            Configure o que cada papel pode <strong>visualizar</strong> e <strong>editar</strong>. As mudanças
            valem para todos os usuários do papel imediatamente.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground">Carregando…</div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground rounded-lg border border-border bg-muted/40 p-2">
              <Info className="w-3.5 h-3.5" />
              Admin sempre tem acesso total (bloqueado). Ajuste apenas os papéis Técnico e Vendedor.
            </div>

            {PERM_GROUPS.map((group) => (
              <div key={group.title} className="rounded-lg border border-border bg-background overflow-hidden">
                <div className="px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {group.title}
                </div>
                <div>
                  <div className="grid grid-cols-[1fr_80px_80px_80px] gap-2 items-center px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                    <div>Permissão</div>
                    {ROLES.map((r) => (
                      <div key={r.key} className={`text-center font-semibold ${r.color}`}>
                        {r.label}
                      </div>
                    ))}
                  </div>
                  {group.items.map((p) => (
                    <div
                      key={p.key}
                      className="grid grid-cols-[1fr_80px_80px_80px] gap-2 items-center px-3 py-2 border-b border-border/60 last:border-b-0"
                      data-testid={`perm-row-${p.key}`}
                    >
                      <div className="text-sm">{p.label}</div>
                      {ROLES.map((r) => (
                        <div key={r.key} className="grid place-items-center">
                          {r.locked ? (
                            <div className="w-4 h-4 grid place-items-center text-muted-foreground" title="Admin: sempre permitido">
                              <Lock className="w-3 h-3" />
                            </div>
                          ) : (
                            <Checkbox
                              checked={has(r.key, p.key)}
                              onCheckedChange={() => toggle(r.key, p.key)}
                              data-testid={`perm-${r.key}-${p.key}`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center gap-2 flex-wrap">
              {ROLES.map((r) => (
                <Badge key={r.key} variant="outline" className={r.color}>
                  {r.label}: {(roles[r.key] || []).length} permissão(ões)
                </Badge>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {dirty && (
            <Button variant="ghost" onClick={handleReset} data-testid="btn-reset-permissoes">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Desfazer
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button onClick={handleSave} disabled={saving || !dirty} data-testid="btn-save-permissoes">
            <Save className="w-4 h-4 mr-1.5" /> Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
